import { useChannelAdminContext } from 'hooks';
import { updateProductMutation } from '../helpers/updateProductMutation';
import styles from './CreateProductForm.scss';
import { routes } from 'lane-shared/config';
import { CreateProductFormInputFields } from './CreateProductFormInputFields';
import { CreateProductSummary } from './CreateProductSummary';
import { Button, ErrorMessage } from 'components';
import { GraphQLError } from 'graphql';
import useUserLocale from 'hooks/useUserLocale';
import { useBillingPaymentSettingsForChannel } from 'lane-shared/domains/billingPayments/hooks';
import { NewProductDetails, TaxPolicy, ProductType } from 'application-types';
import { currencyToPaymentCurrency } from 'domains-billing-web-mappers';
import { AdminPage, PageHeader } from 'components/layout';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { isProductFormValid } from './helpers/isProductFormValid';
import { useLoadProduct } from './hooks/useLoadProduct';
import { sanitizeProductValue } from './helpers/sanitizeProductValue';
import { H4 } from 'design-system-web';
import { CreditPricingSummary } from './CreditPricingSummary';
import {
  useBillingAndPaymentsServicesCRUDEnabled,
  useBpMarkupChargeCodeEnabled,
  useBpAdminFeeChargeCodeEnabled,
  useProductPricing,
} from 'react-hooks';

type Props = {
  breadcrumbs?: {
    label: string;
    url?: string;
  }[];
};

export function EditProductForm({ breadcrumbs }: Props) {
  const { channel } = useChannelAdminContext();
  const { id, productId }: { id: string; productId: string } = useParams();
  const history = useHistory();
  const locale = useUserLocale();
  const [pageError, setPageError] = useState<
    readonly GraphQLError[] | Error | undefined
  >(undefined);
  const { productDetails: loadedProductDetails, product } = useLoadProduct(
    productId,
    channel?._id
  );
  const {
    paymentSettingsError,
    taxDetails,
    currency,
    markupChargeCodeId: rawDefaultMarkupChargeCodeId,
    adminFeeChargeCodeId: rawDefaultAdminFeeChargeCodeId,
  } = useBillingPaymentSettingsForChannel({ channelId: channel?._id });

  const inclusivePricing = taxDetails?.taxPolicy === TaxPolicy.INCLUSIVE;
  const { t } = useTranslation();
  const isBpMarkupChargeCodeEnabled = useBpMarkupChargeCodeEnabled();
  const isBillingAndPaymentsServicesCRUDEnabled =
    useBillingAndPaymentsServicesCRUDEnabled();
  const isBpAdminFeeChargeCodeEnabled = useBpAdminFeeChargeCodeEnabled();
  const [newProductDetails, setNewProductDetails] = useState<NewProductDetails>(
    loadedProductDetails || {
      productCategoryId: '',
      productCategoryName: '',
      productType: ProductType.PRODUCT_TYPE_SERVICE_LABOUR,
      chargeCodeId: undefined,
      name: '',
      description: '',
      rate: '',
      markup: undefined,
      tax: undefined,
      taxDefault: undefined,
    }
  );
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const paymentCurrency = currencyToPaymentCurrency(currency);
  const pricing = useProductPricing({
    priceInput: newProductDetails,
    currency: paymentCurrency,
    locale,
    inclusivePricing,
  });

  useEffect(() => {
    if (loadedProductDetails) {
      setNewProductDetails(loadedProductDetails);
    }
  }, [loadedProductDetails]);

  useEffect(() => {
    setIsSaveDisabled(
      !isProductFormValid(newProductDetails, {
        inclusivePricing,
        requireCategory: isBillingAndPaymentsServicesCRUDEnabled,
        validateCreditAmount: false,
        isReportMarkupEnabled:
          isBpMarkupChargeCodeEnabled && !!rawDefaultMarkupChargeCodeId,
        isReportAdminFeeEnabled:
          isBpAdminFeeChargeCodeEnabled && !!rawDefaultAdminFeeChargeCodeId,
      })
    );
  }, [
    newProductDetails,
    rawDefaultMarkupChargeCodeId,
    rawDefaultAdminFeeChargeCodeId,
    isBpMarkupChargeCodeEnabled,
    isBpAdminFeeChargeCodeEnabled,
    inclusivePricing,
    isBillingAndPaymentsServicesCRUDEnabled,
  ]);

  if (!channel) return null;

  if (!product) return null;

  const handleProductDetailsUpdate = (value: any, field: string) => {
    const sanitizedValue = sanitizeProductValue(value, field);

    setNewProductDetails(prev => {
      const updatedDetails = { ...prev, [field]: sanitizedValue };

      setIsSaveDisabled(isSaveButtonDisabled(updatedDetails));

      return updatedDetails;
    });
  };

  const handleSaveProduct = async () => {
    const { rate, grossTax, grossMarkup, listPrice, netPrice, grossAdminFee } =
      pricing;

    newProductDetails.rate = rate.toString();

    try {
      const result = await updateProductMutation(
        productId,
        newProductDetails,
        { grossTax, grossMarkup, listPrice, netPrice, grossAdminFee },
        channel?._id || ''
      );

      if (result.errors) {
        setPageError(result.errors);
      }

      window.Toast.show(t('abp.productsServices.editProduct.successToast'));
      history.goBack();
    } catch (err: any) {
      window.Toast.show(t('abp.productsServices.editProduct.failToast'));
      setPageError(err);
    }
  };

  const handleCancel = async () => {
    history.goBack();
  };

  const isSaveButtonDisabled = (details = newProductDetails) => {
    return !isProductFormValid(details, {
      inclusivePricing,
      requireCategory: isBillingAndPaymentsServicesCRUDEnabled,
      validateCreditAmount: false,
      isReportMarkupEnabled:
        isBpMarkupChargeCodeEnabled && !!rawDefaultMarkupChargeCodeId,
      isReportAdminFeeEnabled:
        isBpAdminFeeChargeCodeEnabled && !!rawDefaultAdminFeeChargeCodeId,
    });
  };

  return (
    <AdminPage>
      <PageHeader
        header={t(
          `abp.productsServices.editProduct.heading.${newProductDetails.productType}`
        )}
        headerLevel="h3"
        breadcrumbs={
          breadcrumbs ?? [
            {
              label: t('adp.productsServices.breadcrumb'),
              url: routes.channelAdminProductsServices.replace(':id', id),
            },
            {
              label: t(
                `abp.productsServices.editProduct.breadcrumb.${newProductDetails.productType}`
              ),
            },
          ]
        }
      />
      <div className={styles.createProductPage}>
        <CreateProductFormInputFields
          productDetails={newProductDetails}
          updateProductDetails={handleProductDetailsUpdate}
          inclusivePricing={inclusivePricing}
          currency={paymentCurrency}
          edit
        />
        <div className={styles.productPricingSummary}>
          <H4>
            {t('abp.productsServices.createProduct.pricingSummary.title')}
          </H4>
          <CreateProductSummary
            productDetails={newProductDetails}
            currency={paymentCurrency}
            inclusivePricing={inclusivePricing}
            isSaveDisabled={isSaveDisabled}
            setIsSaveDisabled={setIsSaveDisabled}
          />
          {!!newProductDetails.creditAmount && (
            <CreditPricingSummary
              creditAmount={newProductDetails.creditAmount}
            />
          )}
        </div>
      </div>
      <div className={styles.buttonAndErrorGroup}>
        {pageError && <ErrorMessage error={pageError} />}
        {paymentSettingsError && <ErrorMessage error={paymentSettingsError} />}
        <div className={styles.buttonGroup}>
          <Button
            testId="saveNewProductButton"
            variant="contained"
            onClick={() => handleSaveProduct()}
            disabled={isSaveDisabled}
          >
            {t('abp.productsServices.createProduct.save')}
          </Button>
          <Button onClick={handleCancel}>{t('abp.cancel')}</Button>
        </div>
      </div>
    </AdminPage>
  );
}
